/*
 * sq-motion.js — SquadRig shared motion layer (anime.js v3, loaded separately as assets/anime.min.js)
 * Header entrance/hover/scroll state + hero entrance/parallax. All effects are opt-in per element
 * via data-sq-motion hooks and are fully skipped under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasAnime = typeof window.anime === 'function';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ---------------------------------------------------------------------
   * Header: entrance stagger + hover micro-interactions + scroll state
   * ------------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('.header-3');
    if (!header) return;

    var navItems = header.querySelectorAll('.header__linklist-item');
    var iconItems = header.querySelectorAll('.header__icon-wrapper, .sq-account-cyber, .sq-cart-ml, .sq-theme-switch');

    if (!reduceMotion && hasAnime) {
      anime.set(navItems, { opacity: 0, translateY: -6 });
      anime.set(iconItems, { opacity: 0, translateY: -6 });
      anime.timeline({ easing: 'easeOutQuad' })
        .add({ targets: navItems, opacity: [0, 1], translateY: [-6, 0], duration: 420, delay: anime.stagger(45) })
        .add({ targets: iconItems, opacity: [0, 1], translateY: [-6, 0], duration: 360, delay: anime.stagger(35) }, '-=280');

      navItems.forEach(function (item) {
        var link = item.querySelector('.header__linklist-link');
        if (!link) return;
        link.addEventListener('mouseenter', function () {
          anime({ targets: link, scale: [1, 1.04], duration: 220, easing: 'easeOutBack' });
        });
        link.addEventListener('mouseleave', function () {
          anime({ targets: link, scale: [1.04, 1], duration: 220, easing: 'easeOutQuad' });
        });
      });
    }

    /* Scroll state: soft blur + tighten past 40px, independent of anime.js availability */
    var lastState = false;
    function onScroll() {
      var scrolled = window.scrollY > 40;
      if (scrolled !== lastState) {
        header.classList.toggle('sq-header--scrolled', scrolled);
        lastState = scrolled;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------------------
   * Hero: staggered entrance, SVG corner draw-in, pointer-parallax glow
   * ------------------------------------------------------------------- */
  function initHero() {
    var hero = document.querySelector('.sq-hero-brand-section');
    if (!hero) return;

    var animateFlag = hero.getAttribute('data-animate') === 'true';

    if (animateFlag && !reduceMotion && hasAnime) {
      var kicker = hero.querySelector('.sq-hero-kicker');
      var titleLines = hero.querySelectorAll('.sq-hero-title .sq-hero-line');
      var sub = hero.querySelector('.sq-hero-sub');
      var ctas = hero.querySelectorAll('.sq-hero-btn');
      var stats = hero.querySelectorAll('.sq-hero-stat');
      var corners = hero.querySelectorAll('.sq-hero-corners path');

      var tl = anime.timeline({ easing: 'easeOutExpo' });
      if (kicker) { anime.set(kicker, { opacity: 0, translateY: 14 }); tl.add({ targets: kicker, opacity: [0, 1], translateY: [14, 0], duration: 500 }); }
      if (titleLines.length) { anime.set(titleLines, { opacity: 0, translateY: 18 }); tl.add({ targets: titleLines, opacity: [0, 1], translateY: [18, 0], duration: 600, delay: anime.stagger(90) }, kicker ? '-=280' : 0); }
      if (sub) { anime.set(sub, { opacity: 0, translateY: 14 }); tl.add({ targets: sub, opacity: [0, 1], translateY: [14, 0], duration: 500 }, '-=320'); }
      if (ctas.length) { anime.set(ctas, { opacity: 0, translateY: 12 }); tl.add({ targets: ctas, opacity: [0, 1], translateY: [12, 0], duration: 450, delay: anime.stagger(80) }, '-=260'); }
      if (stats.length) { anime.set(stats, { opacity: 0, translateY: 10 }); tl.add({ targets: stats, opacity: [0, 1], translateY: [10, 0], duration: 420, delay: anime.stagger(70) }, '-=200'); }

      if (corners.length) {
        corners.forEach(function (path) {
          var len = path.getTotalLength ? path.getTotalLength() : 40;
          path.style.strokeDasharray = len;
          path.style.strokeDashoffset = len;
        });
        tl.add({ targets: corners, strokeDashoffset: [anime.setDashoffset, 0], duration: 700, delay: anime.stagger(80), easing: 'easeInOutSine' }, 0);
      }
    }

    /* Pointer-parallax on glow orbs: desktop + fine pointer only.
       Tracked against .sq-hero-wrap (the visible viewport-height box), not the
       outer element, since the outer element is a tall scroll spacer in scrub mode. */
    var glow = hero.querySelector('.sq-hero-glow');
    var wrap = hero.querySelector('.sq-hero-wrap') || hero;
    var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (glow && canHover && !reduceMotion) {
      var raf = null;
      wrap.addEventListener('mousemove', function (e) {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          var rect = wrap.getBoundingClientRect();
          var px = (e.clientX - rect.left) / rect.width - 0.5;
          var py = (e.clientY - rect.top) / rect.height - 0.5;
          var x = px * 24;
          var y = py * 24;
          if (hasAnime) {
            anime({ targets: glow, translateX: x, translateY: y, duration: 600, easing: 'easeOutQuad' });
          } else {
            glow.style.transform = 'translate(' + x + 'px,' + y + 'px)';
          }
          raf = null;
        });
      });
      wrap.addEventListener('mouseleave', function () {
        if (hasAnime) {
          anime({ targets: glow, translateX: 0, translateY: 0, duration: 600, easing: 'easeOutQuad' });
        } else {
          glow.style.transform = '';
        }
      });
    }
  }

  /* ---------------------------------------------------------------------
   * Sitewide scroll-reveal engine — opt-in, works on any section.
   *
   *   class="sq-reveal"          -> fades/rises in on its own once in view
   *   class="sq-reveal-stagger"  -> reveals its direct children one by one
   *
   * No markup restructuring needed elsewhere: just add these classes to a
   * section's existing wrapper/item elements. Falls back to instantly
   * visible (no animation, no observer) under prefers-reduced-motion.
   * ------------------------------------------------------------------- */
  function initScrollReveal() {
    var singles = document.querySelectorAll('.sq-reveal');
    var staggerGroups = document.querySelectorAll('.sq-reveal-stagger');

    if (reduceMotion) {
      singles.forEach(function (el) { el.classList.add('sq-revealed'); });
      staggerGroups.forEach(function (el) { el.classList.add('sq-revealed'); });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      singles.forEach(function (el) { el.classList.add('sq-revealed'); });
      staggerGroups.forEach(function (el) { el.classList.add('sq-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);

        if (el.classList.contains('sq-reveal-stagger')) {
          var children = el.children;
          if (hasAnime) {
            anime({
              targets: children,
              opacity: [0, 1],
              translateY: [20, 0],
              duration: 550,
              delay: anime.stagger(70),
              easing: 'easeOutQuad'
            });
          }
        } else if (hasAnime) {
          anime({ targets: el, opacity: [0, 1], translateY: [20, 0], duration: 550, easing: 'easeOutQuad' });
        }
        el.classList.add('sq-revealed');
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    /* Only hide via JS, right before observing — if JS never runs, content
       simply stays at its natural (visible) CSS state. No CSS-only hiding. */
    if (hasAnime) {
      anime.set(singles, { opacity: 0, translateY: 20 });
      staggerGroups.forEach(function (el) { anime.set(el.children, { opacity: 0, translateY: 20 }); });
    }
    singles.forEach(function (el) { io.observe(el); });
    staggerGroups.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------------
   * Home page, desktop only: fade every section in as it scrolls into view.
   *
   * Mobile (<=989px) already does exactly this in assets/mobile-enhancements.js
   * via the CSS `.shopify-section.sq-reveal` / `.is-revealed` pair; that CSS is
   * wrapped in a max-width media query, so desktop had nothing. This closes the
   * gap with the same guard rules (skip the first section, the header, and
   * anything already on screen at load) instead of inventing new behaviour.
   *
   * Deliberately does NOT reuse initScrollReveal's observer: that one uses
   * threshold 0.15, which a section taller than the viewport can never reach —
   * it would stay stuck at opacity 0. Whole sections need threshold 0.
   *
   * Hiding happens in JS right before observing, never in CSS, so if this file
   * fails to load or anime.js is missing the page simply renders as normal.
   * ------------------------------------------------------------------- */
  function initHomeSectionReveal() {
    if (reduceMotion || !hasAnime) return;
    if (!document.body || !document.body.classList.contains('template-index')) return;
    if (!('IntersectionObserver' in window)) return;
    // Below this width the mobile CSS system owns the reveal — don't double up.
    if (window.matchMedia && window.matchMedia('(max-width: 989px)').matches) return;

    var sections = document.querySelectorAll('.shopify-section');
    var targets = [];

    for (var i = 0; i < sections.length; i++) {
      var sec = sections[i];
      if (i === 0) continue;                                           // hero
      if (sec.classList.contains('shopify-section-header')) continue;  // header
      if (sec.classList.contains('sq-reveal')) continue;                // already opted in
      if (sec.hasAttribute('data-sq-reveal')) continue;                 // sq-reveal.js owns it
      // Reveals its own parts already (.sq-reveal / .sq-reveal-stagger here,
      // [data-sq-reveal] over in assets/sq-reveal.js) — don't animate twice.
      if (sec.querySelector('.sq-reveal, .sq-reveal-stagger, [data-sq-reveal]')) continue;
      var rect = sec.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) continue;  // already on screen
      targets.push(sec);
    }
    if (!targets.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        anime({
          targets: el,
          opacity: [0, 1],
          translateY: [24, 0],
          duration: 620,
          easing: 'easeOutQuad',
          complete: function () {
            /* Drop the inline transform: a lingering one makes the section a
               containing block and would break any position:fixed/sticky child. */
            el.style.opacity = '';
            el.style.transform = '';
            el.style.willChange = '';
          }
        });
      });
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });

    targets.forEach(function (el) {
      anime.set(el, { opacity: 0, translateY: 24 });
      el.style.willChange = 'opacity, transform';
      io.observe(el);
    });
  }

  ready(function () {
    initHeader();
    initHero();
    initScrollReveal();
    initHomeSectionReveal();
  });
})();
